import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import firebase from '../config/firebase.config';
import _ from 'lodash';

const database = firebase.firestore();

const DEFAULT_ALERT_CONFIG = {
  enabled: false,
  productivityPercentageThreshold: 50,
  lateLoginAlertMinutes: 30,
};

/**
 * Custom hook to fetch and cache the company's alertConfig setting from
 * companyDatabase. Mirrors useWorkingSchedule's cache-first + onSnapshot
 * pattern for the sibling `alertConfig` object field.
 *
 * @param {string} [overrideCompanyId] - When set (non-empty), read the config for this company instead of the user's active company.
 * @returns {{ alertConfig: { enabled: boolean, productivityPercentageThreshold: number, lateLoginAlertMinutes: number }, loading: boolean }}
 */
export function useAlertConfig(overrideCompanyId) {
  const userProfile = useSelector((state) => state.auth.profile);
  const activeCompanyId = !_.isEmpty(userProfile?.activeCompany)
    ? userProfile?.activeCompany?.companyId
    : null;
  const companyId =
    typeof overrideCompanyId === 'string' && overrideCompanyId.trim() !== ''
      ? overrideCompanyId.trim()
      : activeCompanyId;

  const [alertConfig, setAlertConfig] = useState(DEFAULT_ALERT_CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) {
      setAlertConfig(DEFAULT_ALERT_CONFIG);
      setLoading(false);
      return;
    }

    const cacheKey = `alertConfig_${companyId}`;
    const cachedValue = localStorage.getItem(cacheKey);

    if (cachedValue !== null) {
      try {
        setAlertConfig(JSON.parse(cachedValue));
        setLoading(false);
      } catch {
        // Ignore a malformed cache entry - the snapshot below will replace it.
      }
    }

    const companyRef = database.collection('companyDatabase').doc(companyId);

    const unsubscribe = companyRef.onSnapshot(
      (doc) => {
        const config = doc.data()?.alertConfig;
        const resolved = {
          enabled: config?.enabled ?? DEFAULT_ALERT_CONFIG.enabled,
          productivityPercentageThreshold:
            config?.productivityPercentageThreshold ??
            DEFAULT_ALERT_CONFIG.productivityPercentageThreshold,
          lateLoginAlertMinutes:
            config?.lateLoginAlertMinutes ??
            DEFAULT_ALERT_CONFIG.lateLoginAlertMinutes,
        };

        // The whole companyDatabase doc re-fires this listener whenever ANY
        // sibling field (workingSchedule, stealthMode, ...) is saved, not
        // just alertConfig. Bail out to the same object when the resolved
        // value hasn't actually changed, so consumers that sync local
        // editable state off this value (e.g. an in-progress, unsaved edit)
        // don't get clobbered by an unrelated save elsewhere on the page.
        setAlertConfig((prev) =>
          prev.enabled === resolved.enabled &&
          prev.productivityPercentageThreshold ===
            resolved.productivityPercentageThreshold &&
          prev.lateLoginAlertMinutes === resolved.lateLoginAlertMinutes
            ? prev
            : resolved
        );
        localStorage.setItem(cacheKey, JSON.stringify(resolved));
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching alert config:', error);
        if (cachedValue === null) {
          setAlertConfig(DEFAULT_ALERT_CONFIG);
        }
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [companyId]);

  return { alertConfig, loading };
}
